/**
 * The registry of real-world theatres: ids, titles, era, coverage and the belligerent nations with
 * their names, adjectives and colours. Kept apart from the map data so the build script can read
 * it before any data exists. See index.ts and docs/Theatres.md.
 */
import type { MapSize, Season } from '../core/types';
import type { FactionIdentity } from '../mapgen/names';

export type TheatreId =
  | 'poland-1939'
  | 'east-prussia-1914'
  | 'europe-1939'
  | 'moscow-1941'
  | 'stalingrad-1942'
  | 'normandy-1944';

export interface TheatreNation extends FactionIdentity {
  color: number;
}

export interface TheatreDef {
  id: TheatreId;
  /** Map title (English; the client presents it per language). */
  name: string;
  /** Year the borders, names and cities depict. */
  year: number;
  /** Area covered: west, south, east, north (degrees). */
  bbox: readonly [number, number, number, number];
  /** Size class for menus and `SessionConfig.mapSize` (the board itself is the theatre's own). */
  mapSize: MapSize;
  /** Season of the historical campaign (setups may choose another). */
  season: Season;
  /**
   * Belligerents by faction id. The side a scenario briefs comes first: scenario slot i plays
   * nation i (scenarios.ts), and slot 0 is the player.
   */
  nations: readonly TheatreNation[];
  /**
   * Factions whose starting territory legitimately falls apart into pieces that border an enemy
   * (East Prussia behind the Polish Corridor). Pieces out of reach — islands, or land bordering
   * only neutrals (Albania) — are allowed for everyone as long as they hold no city.
   */
  exclaves?: readonly number[];
  /**
   * The historical alliances, as a team per nation (same index as `nations`): the default teams
   * of a free setup on this theatre (players may change them; scenarios set their own).
   */
  alliances?: readonly number[];
}

// Faction colours: muted staff-map tones, distinct within every theatre.
const GREY = 0x5a6470;
const CRIMSON = 0xb23a2e;
const IMPERIAL_GREEN = 0x3d6b4f;
const BLUE = 0x2f62a8;
const WINE = 0x7a2335;
const GREEN = 0x5b7a3a;
const OCHRE = 0xc2952c;
const OLIVE = 0x5d6e2e;
const KHAKI = 0xa0682c;

const GERMANY: TheatreNation = { name: 'Germany', adjective: 'German', color: GREY };
const POLAND: TheatreNation = { name: 'Poland', adjective: 'Polish', color: CRIMSON };
const FRANCE: TheatreNation = { name: 'France', adjective: 'French', color: BLUE };
const SOVIET_UNION: TheatreNation = { name: 'Soviet Union', adjective: 'Soviet', color: WINE };
const ITALY: TheatreNation = { name: 'Italy', adjective: 'Italian', color: GREEN };
const ROMANIA: TheatreNation = { name: 'Romania', adjective: 'Romanian', color: OCHRE };
const UNITED_STATES: TheatreNation = { name: 'United States', adjective: 'American', color: OLIVE };
const UNITED_KINGDOM: TheatreNation = { name: 'United Kingdom', adjective: 'British', color: KHAKI };
const GERMAN_EMPIRE: TheatreNation = { name: 'German Empire', adjective: 'German', color: GREY };
const RUSSIAN_EMPIRE: TheatreNation = { name: 'Russian Empire', adjective: 'Russian', color: IMPERIAL_GREEN };

export const THEATRES: Record<TheatreId, TheatreDef> = {
  'poland-1939': {
    id: 'poland-1939',
    name: 'Poland, September 1939',
    year: 1939,
    bbox: [12.3, 48.2, 27.9, 55.9],
    mapSize: 'medium',
    season: 'summer',
    nations: [POLAND, GERMANY],
    exclaves: [1],
  },
  'east-prussia-1914': {
    id: 'east-prussia-1914',
    name: 'East Prussia, August 1914',
    year: 1914,
    bbox: [18.6, 52.15, 24.6, 55.85],
    mapSize: 'medium',
    season: 'summer',
    nations: [GERMAN_EMPIRE, RUSSIAN_EMPIRE],
  },
  'europe-1939': {
    id: 'europe-1939',
    name: 'Europe, 1939',
    year: 1939,
    bbox: [-9.8, 36.0, 40.5, 60.2],
    mapSize: 'large',
    season: 'summer',
    nations: [GERMANY, FRANCE, POLAND, SOVIET_UNION, ITALY, UNITED_KINGDOM],
    // The Axis (Germany, Italy) · the Allies of September 1939 (France, Poland, Britain) · the
    // Soviet Union on its own.
    alliances: [0, 1, 1, 2, 0, 1],
    // East Prussia behind the Corridor (Albania, Northern Ireland and the islands are simply out of reach).
    exclaves: [0],
  },
  'moscow-1941': {
    id: 'moscow-1941',
    name: 'Moscow, October 1941',
    year: 1941,
    bbox: [30.0, 52.6, 40.2, 57.6],
    mapSize: 'medium',
    season: 'autumn',
    nations: [SOVIET_UNION, GERMANY],
  },
  'stalingrad-1942': {
    id: 'stalingrad-1942',
    name: 'Stalingrad, November 1942',
    year: 1942,
    bbox: [39.5, 46.4, 47.6, 50.8],
    mapSize: 'large',
    season: 'winter',
    nations: [SOVIET_UNION, GERMANY, ROMANIA, ITALY],
    alliances: [0, 1, 1, 1],
    // The Romanian 3rd Army on the Don and the 4th south of Stalingrad, the 6th Army between them.
    exclaves: [2],
  },
  'normandy-1944': {
    id: 'normandy-1944',
    name: 'Normandy, July 1944',
    year: 1944,
    bbox: [-2.0, 47.9, 2.7, 51.05],
    mapSize: 'medium',
    season: 'summer',
    nations: [UNITED_STATES, UNITED_KINGDOM, GERMANY],
    alliances: [0, 0, 1],
  },
};

export const THEATRE_IDS = Object.keys(THEATRES) as TheatreId[];
